/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import dayjs from 'dayjs';
import * as queryString from 'query-string';
import styled from 'styled-components';
import { Button, Label, List, ListItem, Text } from 'components';
import { theme, toRem } from 'design';
import { routeForPaymentDetail, URL } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getPaymentRequest, Payment, selectPayment } from 'store/Payment';
import { IStore } from 'store/types';
import { DisbursementMethod, PayoutRefType } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay } from 'utils/currency';
import { SummaryContent, SummaryDescription, SummaryFooter, SummaryGrid, SummaryHeader, SummaryLayout } from './components';

const HeaderListItem = styled(ListItem)`
  font-size: ${toRem(20)};
  font-weight: ${theme.font.weight.semiBold};

  label {
    font-size: ${toRem(20)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

const StyledListItem = styled(ListItem)`
  border-bottom: 0;
  height: ${toRem(44)};

  &.top-item {
    margin-top: ${toRem(8)};
    padding-top: ${toRem(12)};
    border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
  }
`;

const PaymentSummary = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { paymentId } = queryString.parse(location.search);
  const payment = useSelector(selectPayment());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.GET_PAYMENT_REQUEST));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (paymentId) dispatch(getPaymentRequest(paymentId.toString(), true));
  }, [paymentId, dispatch]);

  const payouts = payment?.payouts;
  const createdBy = payment?.createdBy;
  const paymentID = payment?.id;
  const isAcceptancePayment = payment?.refType === PayoutRefType.ACCEPTANCE;

  // Data
  const totalInstantPaid = sumBy(
    payouts?.filter((p) => p.disbursementMethod === DisbursementMethod.INSTANT),
    'net'
  );
  const totalPayrollPaid = sumBy(
    payouts?.filter((p) => p.disbursementMethod === DisbursementMethod.PAYROLL),
    'gross'
  );

  const holdbacks = sumBy(payouts, 'holdback');
  const totalTips = sumBy(payouts, 'gross');
  const totalNet = sumBy(payouts, 'net');

  const payeesNumber = payouts && payouts.length;
  const description = payment?.description || '';
  const businessDay = payment?.paymentDate;

  // UI State
  const siteUsesHoldback = holdbacks > 0;
  const userName = createdBy?.name || '';

  return (
    <SummaryLayout>
      <SummaryHeader isLoading={isLoading} totalNet={centsToDollarsDisplay(totalNet)} />

      <SummaryGrid>
        <SummaryContent>
          <List>
            <HeaderListItem data-cy="total-paid" isLoading={isLoading} label="Paid Now" value={centsToDollarsDisplay(totalInstantPaid)} />
            {!isAcceptancePayment && <StyledListItem data-cy="gross" isLoading={isLoading} label="Gross" value={centsToDollarsDisplay(totalTips)} />}
            {siteUsesHoldback && <StyledListItem data-cy="holdbacks" isLoading={isLoading} label="Holdbacks" value={`-${centsToDollarsDisplay(holdbacks)}`} />}
            <StyledListItem data-cy="total-net" isLoading={isLoading} label={!isAcceptancePayment ? 'Net' : 'Total'} value={centsToDollarsDisplay(totalNet)} />
            <StyledListItem data-cy="total-payroll" isLoading={isLoading} label="Payroll" value={centsToDollarsDisplay(totalPayrollPaid)} />
            <StyledListItem data-cy="total-instant" isLoading={isLoading} label="Instant" value={centsToDollarsDisplay(totalInstantPaid)} />
            <StyledListItem data-cy="payment-id" isLoading={isLoading} label="Payment ID" value={paymentID} className="top-item" />
            <StyledListItem data-cy="number-of-payees" isLoading={isLoading} label="Payees" value={payeesNumber} />
            <StyledListItem data-cy="business-date" isLoading={isLoading} label="Business Day" value={dayjs(businessDay).format('MM-DD-YYYY')} />
            <StyledListItem data-cy="sent-by" isLoading={isLoading} label="Sent By" value={userName} />
            <SummaryDescription>
              <Label>{isLoading ? <Skeleton width={77} /> : 'Description'}</Label>
              <Text data-cy="description">{isLoading ? <Skeleton /> : description || <em>No description provided.</em>}</Text>
            </SummaryDescription>
          </List>
        </SummaryContent>
      </SummaryGrid>

      <SummaryFooter isLoading={isLoading}>
        {isAcceptancePayment ? (
          <Button appearance="secondary" size="large" data-cy="return-to-acceptance-btn" label="Return to Tip Acceptance" to={URL.PaymentsAcceptance} />
        ) : (
          <Button appearance="secondary" size="large" data-cy="make-another-payment-btn" label="Make Another Payment" to={URL.MakePayment} />
        )}
        {paymentID && <Button appearance="primary" size="large" data-cy="view-payment-details-btn" label="View Payment Details" to={routeForPaymentDetail(paymentID)} />}
      </SummaryFooter>
    </SummaryLayout>
  );
};

export default PaymentSummary;
